import { Link } from '@remix-run/react'
import { Badge, Card, CardContent, H3, Icon } from '#app/ui/components'
import {
	EventStatusBadge,
	type EventStatus,
} from '#app/ui/components/events/event-status-badge.tsx'
import { getTicketAvailabilityStatus } from '#app/utils/ticket-availability.ts'
import { formatEventDate } from '#app/utils/timezone.ts'

type TicketStats = {
	totalAvailable: number
	availableNow: number
	sold?: number
	reserved?: number
	minPrice: number | null
	maxPrice: number | null
	hasPaidTickets: boolean
	hasFreeTickets: boolean
}

type EventOccurrence = {
	id: string
	startDate: Date | string
	endDate: Date | string
	cutoffHours: number
	capacity: number
	hasTickets: boolean
	isSoldOut: boolean
	isBookable: boolean
	ticketStats: TicketStats
	utilizationPercentage: number
	timeStatus?: EventStatus
}

type Event = {
	id: string
	name: string
	description?: string | null
	images: Array<{
		id: string
		altText: string | null
	}>
	genres: Array<{
		id?: string
		name: string
	}>
	nightclub?: {
		id: string
		name: string
		timezone: string
	}
	nightclubId?: string
	timezone?: string
}

export type EventCardData = EventOccurrence & {
	event: Event
}

export type VenueEventCardData = Event & {
	nextOccurrence: EventOccurrence | null
	bookableOccurrences: Array<{ id: string }>
}

type EventCardProps = {
	/**
	 * Layout variant:
	 * - 'grid': Vertical card with image on top/bottom (homepage style)
	 * - 'horizontal': Horizontal card with image on left (venue page style)
	 */
	variant: `grid` | `horizontal`
	/**
	 * Event data from homepage (EventOccurrence with nested event)
	 */
	occurrence?: EventCardData
	/**
	 * Event data from venue page (Event with nextOccurrence)
	 */
	venueEvent?: VenueEventCardData
	/**
	 * For grid variant: whether to flip the image position (alternating pattern)
	 */
	isFlipped?: boolean
	/**
	 * Link destination - will be auto-calculated if not provided
	 */
	href?: string
}

/**
 * Formats a date and time for display in the event card
 */
function formatDateAndTime(
	startDate: string | Date,
	timezone: string = `Europe/Madrid`,
): {
	date: string
	time: string
	full: string
} {
	const dateObject =
		typeof startDate === `string` ? new Date(startDate) : startDate

	// Validate date to prevent invalid parsing
	if (isNaN(dateObject.getTime())) {
		throw new Error(`Invalid startDate: ${startDate}`)
	}

	// Use centralized date formatting for consistency
	const date = formatEventDate(dateObject, timezone)

	// Time: "20:00" (24-hour format)
	const timeFormatter = new Intl.DateTimeFormat(`en-US`, {
		hour: `2-digit`,
		minute: `2-digit`,
		hour12: false, // 24-hour format
		timeZone: timezone,
	})
	const time = timeFormatter.format(dateObject) // e.g., "20:00"

	return {
		date, // e.g., "Friday, November 21"
		time, // e.g., "20:00"
		full: date, // For compatibility with venue page
	}
}

/**
 * Gets the price display text for an event
 */
function getPriceDisplay(ticketStats: TicketStats): string {
	const { minPrice, maxPrice, hasPaidTickets, hasFreeTickets } = ticketStats

	if (!hasPaidTickets && hasFreeTickets) return `Free`
	if (!hasPaidTickets && !hasFreeTickets) return `No tickets`
	if (minPrice === maxPrice) return `€${minPrice}`
	return `€${minPrice} - €${maxPrice}`
}

/**
 * Unified Event Card Component
 *
 * Supports two layout variants:
 * - 'grid': Vertical layout used on homepage with alternating image positions
 * - 'horizontal': Horizontal layout used on venue pages with image on left
 */
export function EventCard({
	occurrence,
	venueEvent,
	isFlipped = false,
	href,
}: EventCardProps) {
	// Normalize data - support both data structures
	const event = occurrence?.event || venueEvent
	const eventOccurrence = occurrence || venueEvent?.nextOccurrence

	if (!event || !eventOccurrence) {
		return null
	}

	// Calculate link - always link to the occurrence
	const link =
		href ||
		(occurrence
			? `/events/${event.id}/occurrences/${occurrence.id}`
			: venueEvent?.nextOccurrence
				? `/events/${event.id}/occurrences/${venueEvent.nextOccurrence.id}`
				: `/events/${event.id}/occurrences/${eventOccurrence.id}`)

	// Get formatted data
	const timezone =
		event.nightclub?.timezone || event.timezone || `Europe/Madrid`
	const dateInfo = formatDateAndTime(eventOccurrence.startDate, timezone)
	const priceDisplay = getPriceDisplay(eventOccurrence.ticketStats)

	// Get ticket availability status (separate from event status)
	const ticketAvailability = eventOccurrence.hasTickets
		? getTicketAvailabilityStatus({
				availableNow: eventOccurrence.ticketStats.availableNow,
				totalAvailable: eventOccurrence.ticketStats.totalAvailable,
			})
		: null

	// ALWAYS USE THE SAME GRID LAYOUT
	return (
		<Link to={link} className="block">
			<Card
				role="article"
				aria-label={`Event: ${event.name}`}
				className={`group flex h-full cursor-pointer flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
			>
				{/* Event Image */}
				<div
					className={`relative aspect-video overflow-hidden ${isFlipped ? `order-2` : `order-1`}`}
				>
					{event.images[0] ? (
						<img
							src={`/resources/event-images/${event.images[0].id}`}
							alt={event.images[0].altText || event.name}
							className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
						/>
					) : (
						<div className="flex h-full items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100">
							<Icon
								name="image"
								className="h-16 w-16 text-brand-muted-amethyst"
							/>
						</div>
					)}
				</div>

				{/* Event Details */}
				<CardContent className={`p-6 ${isFlipped ? `order-1` : `order-2`}`}>
					{/* Event Title */}
					<H3>{event.name}</H3>

					{/* Venue and Location */}
					{event.nightclub && (
						<div className="mb-3 flex items-center">
							<Icon name="home" className="mr-2 h-4 w-4" />
							<span className="text-sm">{event.nightclub.name}</span>
						</div>
					)}

					{/* Date */}
					<div className="mb-3 flex items-center">
						<Icon name="calendar" className="mr-2 h-4 w-4" />
						<span className="text-sm">{dateInfo.date}</span>
					</div>

					{/* Time */}
					<div className="mb-3 flex items-center">
						<Icon name="clock" className="mr-2 h-4 w-4" aria-hidden="true" />
						<time
							className="text-sm"
							dateTime={eventOccurrence.startDate.toString()}
						>
							{dateInfo.time}
						</time>
					</div>

					{/* Genres */}
					{event.genres.length > 0 && (
						<div className="mb-4 flex flex-wrap gap-1">
							{event.genres.slice(0, 3).map((genre) => (
								<Badge key={genre.name} variant="default" className="text-xs">
									{genre.name}
								</Badge>
							))}
							{event.genres.length > 3 && (
								<Badge variant="default" className="text-xs">
									+{event.genres.length - 3}
								</Badge>
							)}
						</div>
					)}

					{/* Price and Status */}
					<div className="flex flex-wrap items-center justify-between gap-4">
						<div className="flex items-center gap-4 text-2xl font-light">
							<span>{priceDisplay}</span>

							{/* Show ticket availability badge (Few Left, Sold Out, etc.) 
							"sold-out" | "few-left" | "selling-fast" | "available"
							*/}
							{ticketAvailability && (
								<Badge
									variant={
										ticketAvailability.status === `sold-out`
											? `destructive`
											: ticketAvailability.status === `few-left`
												? `warning`
												: ticketAvailability.status === `selling-fast`
													? `info`
													: `default`
									}
									className="font-medium"
								>
									{ticketAvailability.text}
								</Badge>
							)}

							{/* Show "No tickets" if event has no tickets */}
							{!eventOccurrence.hasTickets && (
								<Badge variant="secondary" className="font-medium">
									No tickets
								</Badge>
							)}
						</div>

						{/* Show event timing status badge (Live Now, Starting in Xh, etc.) */}
						{eventOccurrence.timeStatus && (
							<EventStatusBadge
								timeStatus={eventOccurrence.timeStatus}
								occurrence={eventOccurrence}
							/>
						)}
					</div>
				</CardContent>
			</Card>
		</Link>
	)
}
